// ── SettingsCertificateInstitutionPage ──────────────────────────────────────
//
// #1803 (P1, gating) — Settings → Certificate / Institution Settings.
//
// Tenant-admin surface to edit the institution identity that appears on
// issued certificates: institution name (corrects/refines the platform-
// admin's provisioning-time value) + signatory name/title (replaces the
// certificatePdf.ts hardcoded Momentum signatory). Structurally cloned from
// SettingsTenantBrandingPage.tsx (recon §2 / IMPL step 6): admin-redirect
// guard fail-closed, GET-resolved / PATCH-diff (only changed fields sent,
// snake_case body).
//
// Every field freezes into certificate metadata at generation time — an
// edit here NEVER changes an already-issued certificate (recon §5). The
// confirmation guardrail below (IMPL step 7 — no existing ConfirmDialog
// component in this codebase; the closest precedent is a bare
// window.confirm() at AgentModelSettingsPage.tsx:301) makes that explicit
// before every save.
//
// Logo / signature image upload (migration 0192) — multipart POST, cloned
// from ClaudeDesignBundleUpload.tsx's raw-fetch-plus-FormData pattern (the
// JSON-only apiFetch helper always sets Content-Type: application/json,
// wrong for multipart). Same 2 MiB / PNG+JPEG-only limits as the backend
// (Q8 — do not special-case signature vs logo).

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE } from '../lib/api';
import Sidebar from '../components/Sidebar';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const CONFIRM_COPY =
  'This appears on all FUTURE certificates. Already-issued certificates are unchanged. Confirm?';

const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

interface InstitutionBrandingResponse {
  institutionName: string;
  signatoryName: string | null;
  signatoryTitle: string | null;
  logoMimeType: string | null;
  signatureMimeType: string | null;
  /** Only present on the POST /logo response (OCR double-duty, IMPL Q4/Q5).
   *  null means "no suggestion" — either OCR found nothing usable, its
   *  confidence was below threshold, or the OCR engine itself was
   *  unavailable; the upload still succeeds in every case. */
  institutionNameSuggestion?: string | null;
}

const OCR_NO_SUGGESTION_HINT = "Couldn't read a name from the logo — please enter it.";

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 24,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginBottom: 6, display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px',
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontFamily: 'var(--font-display)', fontSize: 13,
  fontWeight: 600, color: 'white', background: 'var(--primary)',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  padding: '8px 14px', fontFamily: 'var(--font-body)', fontSize: 13,
  fontWeight: 500, color: 'var(--text-secondary)', background: 'transparent',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const hintStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)',
  display: 'block', marginTop: 4,
};

export default function SettingsCertificateInstitutionPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  const [branding, setBranding] = useState<InstitutionBrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [institutionName, setInstitutionName] = useState('');
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('');

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [signatureError, setSignatureError] = useState('');
  // OCR name-suggest (IMPL Q4/Q5) — set after a logo upload; cleared on the
  // next logo upload attempt. Never persisted — purely a pre-Save hint.
  const [ocrHint, setOcrHint] = useState('');

  // Fail-closed redirect for non-admins — mirrors SettingsTenantBrandingPage.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) {
      navigate('/settings');
    }
  }, [tenantLoading, isAdmin, navigate]);

  const applyBranding = useCallback((b: InstitutionBrandingResponse) => {
    setBranding(b);
    setInstitutionName(b.institutionName ?? '');
    setSignatoryName(b.signatoryName ?? '');
    setSignatoryTitle(b.signatoryTitle ?? '');
  }, []);

  const loadBranding = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    apiFetch<InstitutionBrandingResponse>(`/tenants/${tenantId}/institution`)
      .then(applyBranding)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load institution settings'))
      .finally(() => setLoading(false));
  }, [tenantId, applyBranding]);

  useEffect(() => { if (isAdmin) loadBranding(); }, [loadBranding, isAdmin]);

  const markDirty = () => { setSavedAt(null); setError(''); };

  const handleSave = async () => {
    if (!tenantId) return;
    setError('');

    // snake_case body, only-changed-fields diff — same contract as the
    // branding PATCH. institution_name/signatory_name/signatory_title are
    // all independently optional server-side. Signatory fields are
    // nullable (the Zod schema requires min(1) for a non-null value), so a
    // cleared field must send explicit `null`, never `""`.
    const body: Record<string, string | null> = {};
    const name  = institutionName.trim();
    const sName = signatoryName.trim();
    const sTitle = signatoryTitle.trim();
    const loadedName   = branding?.institutionName ?? '';
    const loadedSName  = branding?.signatoryName ?? '';
    const loadedSTitle = branding?.signatoryTitle ?? '';

    // R1/R2 review fix — institution_name is a required field (NOT NULL,
    // min(1) server-side); clearing it to "" and saving used to silently
    // fall through to the generic "No changes to save." message (the
    // `name && ...` check below is falsy on an empty string, so the
    // cleared value was just dropped from the diff instead of being sent
    // and rejected). Catch the real intent explicitly, before it can be
    // confused with "nothing changed."
    if (!name && loadedName) {
      setError('Institution name cannot be empty.');
      return;
    }

    if (name && name !== loadedName) body.institution_name = name;
    if (sName !== loadedSName) body.signatory_name = sName === '' ? null : sName;
    if (sTitle !== loadedSTitle) body.signatory_title = sTitle === '' ? null : sTitle;

    if (Object.keys(body).length === 0) {
      setError('No changes to save.');
      return;
    }

    // Confirmation guardrail (IMPL step 7) — every field edit on this
    // surface is gated behind an explicit "affects future certs only"
    // confirmation before it reaches the network.
    if (!window.confirm(CONFIRM_COPY)) {
      return;
    }

    setSaving(true);
    try {
      const updated = await apiFetch<InstitutionBrandingResponse>(`/tenants/${tenantId}/institution`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      applyBranding(updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (field: 'logo' | 'signature', file: File) => {
    if (!tenantId) return;
    const setUploading = field === 'logo' ? setUploadingLogo : setUploadingSignature;
    const setFieldError = field === 'logo' ? setLogoError : setSignatureError;
    setFieldError('');
    if (field === 'logo') setOcrHint('');

    if (file.size > IMAGE_MAX_BYTES) {
      setFieldError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 2 MB.`);
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setFieldError('Expected a PNG or JPEG image.');
      return;
    }

    if (!window.confirm(CONFIRM_COPY)) {
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append(field, file);
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/tenants/${tenantId}/institution/${field}`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Upload failed: HTTP ${res.status}`);
      }
      const updated = (await res.json()) as InstitutionBrandingResponse;
      applyBranding(updated);
      // IMPL Q4/Q5 — apply the OCR suggestion AFTER applyBranding(), which
      // resets institutionName to the persisted (unrelated) value; the
      // suggestion is not persisted anywhere, it only pre-fills the input.
      // The tenant-admin can still overwrite it before clicking Save —
      // nothing here saves automatically. Skip the "saved" banner in this
      // branch: the logo itself did save, but the name field is now
      // holding an unsaved suggestion, and "Institution settings saved"
      // right next to a freshly-dirtied field would be misleading.
      if (field === 'logo' && updated.institutionNameSuggestion) {
        setInstitutionName(updated.institutionNameSuggestion);
        markDirty();
      } else {
        if (field === 'logo') setOcrHint(OCR_NO_SUGGESTION_HINT);
        setSavedAt(Date.now());
      }
    } catch (e) {
      setFieldError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (tenantLoading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p data-testid="cert-institution-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }} data-testid="cert-institution-page">
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <div style={{ marginBottom: 16 }}>
          <Link to="/settings" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Back to settings
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Certificate / Institution Settings
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, maxWidth: 640 }}>
          Controls how your institution appears on issued certificates. Changes apply to
          future certificates only — already-issued certificates are never altered. Only
          tenant admins can edit.
        </p>

        <div style={{ maxWidth: 640 }}>
          {loading ? (
            <div style={cardStyle}>
              <p data-testid="cert-institution-loading" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Loading institution settings…
              </p>
            </div>
          ) : (
            <>
              <div style={cardStyle}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                  Institution identity
                </h2>

                {error && (
                  <div data-testid="cert-institution-error" style={{
                    padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
                    borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
                  }}>{error}</div>
                )}
                {savedAt && !error && (
                  <div data-testid="cert-institution-saved" style={{
                    padding: '8px 12px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
                    borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
                  }}>Institution settings saved. Future certificates only.</div>
                )}

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle} htmlFor="cert-institution-name">Institution name</label>
                  <input
                    id="cert-institution-name"
                    data-testid="cert-institution-input-name"
                    type="text"
                    value={institutionName}
                    onChange={e => { setInstitutionName(e.target.value); markDirty(); }}
                    disabled={saving}
                    maxLength={255}
                    aria-describedby="cert-institution-name-hint"
                    style={inputStyle}
                  />
                  <span id="cert-institution-name-hint" style={hintStyle}>
                    Rendered on the certificate and its verify page. Corrects/refines the name set at provisioning.
                  </span>
                  {ocrHint && (
                    <span data-testid="cert-institution-ocr-hint" style={{ ...hintStyle, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      {ocrHint}
                    </span>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle} htmlFor="cert-signatory-name">Signatory name</label>
                  <input
                    id="cert-signatory-name"
                    data-testid="cert-institution-input-signatoryName"
                    type="text"
                    value={signatoryName}
                    onChange={e => { setSignatoryName(e.target.value); markDirty(); }}
                    disabled={saving}
                    maxLength={255}
                    placeholder="e.g. Dr. Jane Smith"
                    aria-describedby="cert-signatory-name-hint"
                    style={inputStyle}
                  />
                  <span id="cert-signatory-name-hint" style={hintStyle}>Who signs the certificate.</span>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle} htmlFor="cert-signatory-title">Signatory title</label>
                  <input
                    id="cert-signatory-title"
                    data-testid="cert-institution-input-signatoryTitle"
                    type="text"
                    value={signatoryTitle}
                    onChange={e => { setSignatoryTitle(e.target.value); markDirty(); }}
                    disabled={saving}
                    maxLength={255}
                    placeholder="e.g. Dean of Engineering"
                    aria-describedby="cert-signatory-title-hint"
                    style={inputStyle}
                  />
                  <span id="cert-signatory-title-hint" style={hintStyle}>Rendered alongside the signatory name.</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    type="button"
                    data-testid="cert-institution-save"
                    onClick={() => void handleSave()}
                    disabled={saving}
                    style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                  >
                    {saving ? 'Saving…' : 'Save institution settings'}
                  </button>
                </div>
              </div>

              {/* Logo — migration 0192. PNG/JPEG only, 2 MiB max, same
                  confirmation guardrail as the text fields. */}
              <div style={cardStyle} data-testid="cert-institution-logo-card">
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  Institution logo
                </h2>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                  {branding?.logoMimeType
                    ? `A logo is on file (${branding.logoMimeType}).`
                    : 'No logo uploaded yet.'}
                </p>
                {logoError && (
                  <div data-testid="cert-institution-logo-error" style={{
                    padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
                    borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
                  }}>{logoError}</div>
                )}
                <label
                  htmlFor="cert-institution-logo-file"
                  style={{ ...btnGhost, display: 'inline-block', opacity: uploadingLogo ? 0.6 : 1, cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}
                >
                  {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                </label>
                <input
                  id="cert-institution-logo-file"
                  data-testid="cert-institution-logo-upload"
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={uploadingLogo}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = ''; // allow re-selecting the same file
                    if (file) void uploadImage('logo', file);
                  }}
                />
                <span style={{ ...hintStyle, marginTop: 8 }}>PNG or JPEG, up to 2 MB.</span>
              </div>

              {/* Signature — same pattern/limits as the logo (Q8: do not special-case). */}
              <div style={cardStyle} data-testid="cert-institution-signature-card">
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  Signatory signature
                </h2>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                  {branding?.signatureMimeType
                    ? `A signature is on file (${branding.signatureMimeType}).`
                    : 'No signature uploaded yet.'}
                </p>
                {signatureError && (
                  <div data-testid="cert-institution-signature-error" style={{
                    padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
                    borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
                  }}>{signatureError}</div>
                )}
                <label
                  htmlFor="cert-institution-signature-file"
                  style={{ ...btnGhost, display: 'inline-block', opacity: uploadingSignature ? 0.6 : 1, cursor: uploadingSignature ? 'not-allowed' : 'pointer' }}
                >
                  {uploadingSignature ? 'Uploading…' : 'Upload signature'}
                </label>
                <input
                  id="cert-institution-signature-file"
                  data-testid="cert-institution-signature-upload"
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={uploadingSignature}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void uploadImage('signature', file);
                  }}
                />
                <span style={{ ...hintStyle, marginTop: 8 }}>PNG or JPEG, up to 2 MB.</span>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
